<script setup lang="ts">
  import { IconAccount, IconCalendarClock } from '@gat-ship/icons'
  import type { TenantUser, ISODateTimeString } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    ActionCard,
    ActionCardDescription,
    ActionCardLeadIcon,
    ActionCardTitle,
    Button,
    FormSubmit,
    DateCalendar,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useAppLoading,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'
  import { ref, computed } from 'vue'
  import { Temporal } from '@js-temporal/polyfill'
  import { globalUtils, useFormatting } from '@gat-ship/utils'
  import {
    tenantUserLoadingIds,
    useTenantUserDetailContext
  } from '../../composables/useTenantUserDetail'
  import { useTenantUserUtils } from '../../composables/useTenantUserUtils'

  const { formatDateTime } = useFormatting()
  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const { TenantUserUtils } = useTenantUserUtils()
  const { setLoadingId, removeLoadingId } = useAppLoading()
  const currentTenantUser = ref<TenantUser | undefined>(undefined)

  const scheduleDeactivationSchema = z.object({
    userId: z.string().min(1),
    deactivateAt: z.string().min(1, 'Deactivation date is required')
  })
  type ScheduleDeactivationFormValues = z.infer<
    typeof scheduleDeactivationSchema
  >

  const form = useForm({
    defaultValues: {
      userId: '',
      deactivateAt: ''
    } as ScheduleDeactivationFormValues,
    validators: {
      onChange: scheduleDeactivationSchema
    },
    onSubmit: async ({ value }) => {
      await tenantUserProvider.commands.scheduleDeactivation({
        userId: value.userId,
        deactivateAt: value.deactivateAt as ISODateTimeString
      })
      isOpen.value = false
    }
  })

  const isScheduled = computed(() =>
    currentTenantUser.value
      ? TenantUserUtils.isScheduledForDeactivation(currentTenantUser.value)
      : false
  )

  // "Cancel scheduled deactivation" action. Mirrors the activation
  // modal's flow: confirm -> close modal -> set a per-user loading id so
  // the users-list row pulses -> call `removeDeactivationSchedule` (sets
  // activeTo to forever). On failure, reopen the modal with the error.
  const showCancelConfirm = ref(false)
  const scheduledDateText = computed(() => {
    if (!currentTenantUser.value?.activeTo) return ''
    return `Will deactivate on ${formatDateTime(currentTenantUser.value.activeTo, 'long')}`
  })
  async function cancelDeactivationSchedule() {
    showCancelConfirm.value = false
    if (!currentTenantUser.value) return
    const userId = currentTenantUser.value.userId
    const loadingId = tenantUserLoadingIds.tenantUser(userId)
    isOpen.value = false
    setLoadingId(loadingId)
    try {
      await tenantUserProvider.commands.removeDeactivationSchedule({
        userId,
        deactivateAt: globalUtils.functions.dateForever()
      })
    } catch {
      isOpen.value = true
    } finally {
      removeLoadingId(loadingId)
    }
  }
  function openCancelConfirm() {
    showCancelConfirm.value = true
  }

  // Bind the inline `<DateCalendar>` directly to the tanstack form
  // field. We store an ISO datetime string (local-midnight -> UTC instant)
  // on the form to match the request shape, while the calendar itself
  // speaks `Temporal.PlainDate`.
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const deactivateAtValue = form.useStore((s) => s.values.deactivateAt)
  const deactivateAtErrors = form.useStore(
    (s) =>
      (s.fieldMeta?.deactivateAt?.errors ?? []) as Array<
        string | { message?: string } | undefined
      >
  )

  const calendarValue = computed<Temporal.PlainDate | null>(() => {
    const v = deactivateAtValue.value
    if (!v) return null
    return Temporal.Instant.from(v)
      .toZonedDateTimeISO(localTimeZone)
      .toPlainDate()
  })

  const firstErrorMessage = computed(() => {
    const e = deactivateAtErrors.value[0]
    if (!e) return undefined
    return typeof e === 'string' ? e : (e.message ?? undefined)
  })

  function onDateSelected(value: unknown) {
    // DateCalendar emits PlainDate | DateRange | null. We use single
    // mode, so a DateRange shape means the wrong mode — ignore it.
    if (!value || typeof value !== 'object' || 'from' in value) {
      form.setFieldValue('deactivateAt', '')
      return
    }
    const date = value as Temporal.PlainDate
    const iso = date
      .toZonedDateTime({
        timeZone: localTimeZone,
        plainTime: Temporal.PlainTime.from('00:00:00')
      })
      .toInstant()
      .toString() as ISODateTimeString
    form.setFieldValue('deactivateAt', iso)
  }

  export interface ShowScheduleDeactivationModalParams {
    tenantUser: TenantUser
    loadingIds?: string[]
  }

  function showModal({ tenantUser }: ShowScheduleDeactivationModalParams) {
    currentTenantUser.value = tenantUser
    form.reset({
      userId: tenantUser.userId,
      deactivateAt: tenantUser.activeTo
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAccount class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Schedule User Deactivation
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        Choose a date when this account will be deactivated. The user will
        remain active until then, and the account and data will stay intact
        after deactivation.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <DateCalendar
          class="w-full"
          :model-value="calendarValue"
          :disabled-ranges="[
            { from: 'epoch', to: Temporal.Now.plainDateISO() }
          ]"
          :data-testid="
            testIds.tenantUser.modal.scheduleDeactivation.deactivationDateInput
          "
          @update:model-value="onDateSelected"
        />
        <ActionCard
          v-if="currentTenantUser && isScheduled"
          @click="openCancelConfirm"
        >
          <ActionCardLeadIcon :icon="IconCalendarClock" />
          <div class="space-y-1">
            <ActionCardTitle> Cancel Deactivation Schedule </ActionCardTitle>
            <ActionCardDescription>
              {{ scheduledDateText }}
            </ActionCardDescription>
          </div>
        </ActionCard>
        <p v-if="firstErrorMessage" class="text-destructive text-xs">
          {{ firstErrorMessage }}
        </p>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit
        :form="form"
        label="Schedule User Deactivation"
        :data-testid="testIds.tenantUser.modal.scheduleDeactivation.saveButton"
        :require-dirty="false"
      />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
  <AlertDialog v-model:open="showCancelConfirm">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle> Cancel Scheduled Deactivation? </AlertDialogTitle>
      </AlertDialogHeader>
      <div class="space-y-2">
        <p class="whitespace-pre-line text-sm leading-tight">
          This will remove the scheduled deactivation.
        </p>
        <p class="whitespace-pre-line text-sm leading-tight">
          Are you sure you want to cancel this scheduled deactivation?
        </p>
      </div>
      <AlertDialogFooter>
        <Button variant="outline" @click="cancelDeactivationSchedule()">
          Confirm
        </Button>
        <Button variant="outline" @click="showCancelConfirm = false">
          Cancel
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
